/**
 * Full Spectrum Center DJ — IndexedDB metadata cache for the track strip.
 *
 * We do NOT store audio bytes (too large). We store a small metadata blob keyed by a
 * SHA-256 hash of the first ~1MB of the file, so the DJ can drop the same file back
 * in next session and instantly get BPM + hot cues restored.
 */

const DB_NAME = "stackroom-dj";
const DB_VERSION = 1;
const STORE = "tracks";

const HASH_BYTES = 1024 * 1024; // 1MB

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const os = db.createObjectStore(STORE, { keyPath: "hash" });
        os.createIndex("addedAt", "addedAt");
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function tx(mode, fn) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const os = t.objectStore(STORE);
    let result;
    Promise.resolve(fn(os)).then((r) => { result = r; }).catch(reject);
    t.oncomplete = () => resolve(result);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
  });
}

const toHex = (buf) =>
  Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, "0")).join("");

/** SHA-256 of the first 1MB of the file (cheap, deterministic). */
export async function hashFile(file) {
  const slice = file.slice(0, HASH_BYTES);
  const buf = await slice.arrayBuffer();
  const digest = await crypto.subtle.digest("SHA-256", buf);
  return toHex(digest);
}

/**
 * Save / update a track's metadata. Pass a minimal subset; missing fields are
 * merged with the existing record so partial updates (e.g. lastLoadedTo) work.
 */
export async function saveTrack(entry) {
  if (!entry || !entry.hash) return;
  return tx("readwrite", async (os) => {
    const prev = await new Promise((res) => {
      const r = os.get(entry.hash); r.onsuccess = () => res(r.result); r.onerror = () => res(null);
    });
    const merged = { ...(prev || {}), ...entry, hash: entry.hash, addedAt: prev?.addedAt || Date.now() };
    os.put(merged);
  });
}

/** Return all stored tracks, newest first. */
export async function listGhosts() {
  return tx("readonly", (os) =>
    new Promise((res) => {
      const out = [];
      const r = os.openCursor();
      r.onsuccess = (e) => {
        const c = e.target.result;
        if (c) { out.push(c.value); c.continue(); }
        else { out.sort((a, b) => (b.addedAt || 0) - (a.addedAt || 0)); res(out); }
      };
      r.onerror = () => res([]);
    })
  );
}

export async function getByHash(hash) {
  return tx("readonly", (os) =>
    new Promise((res) => { const r = os.get(hash); r.onsuccess = () => res(r.result || null); r.onerror = () => res(null); })
  );
}

export async function removeGhost(hash) {
  return tx("readwrite", (os) => os.delete(hash));
}
